<script lang="ts">
  /**
   * BlogPost layout - Wraps markdown blog posts with consistent styling.
   *
   * Receives frontmatter props from mdsvex:
   * - title: Post title
   * - date: Publication date (YYYY-MM-DD)
   * - author: Author name
   * - description: Short description for meta/previews
   * - image: Optional hero image path
   * - tags: Optional array of tags
   */
  import GridLines from '$lib/components/GridLines.svelte';
  import GridContent from '$lib/components/GridContent.svelte';
  import Star from '$lib/components/Star.svelte';
  import { hatchStyle45 } from '$lib/styles/hatch';
  import type { Snippet } from 'svelte';

  interface Props {
    title: string;
    date: string;
    author?: string;
    description?: string;
    image?: string;
    tags?: string[];
    children: Snippet;
  }

  let {
    title,
    date,
    author = 'Corral Team',
    description,
    image,
    tags = [],
    children
  }: Props = $props();

  // Format date for display
  const formattedDate = $derived(
    new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  );
</script>

<svelte:head>
  <title>{title} - Corral AI News</title>
  {#if description}
    <meta name="description" content={description} />
  {/if}
</svelte:head>

<!-- Hero Section -->
<section
  class="bg-[#f9f9f4] pt-16 pb-24 relative"
  style={hatchStyle45}
>
  <GridLines variant="light" />
  <GridContent>
    <div class="max-w-[800px] mx-auto">
      <!-- Back link -->
      <a
        href="/news"
        class="inline-flex items-center gap-2 font-mono text-[14px] text-[#5C5855] hover:text-[#ee6018] transition-colors mb-8"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M10 4L6 8L10 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        Back to News
      </a>

      <!-- Tags -->
      {#if tags.length > 0}
        <div class="flex flex-wrap gap-2 mb-4">
          {#each tags as tag}
            <span class="font-mono text-[12px] text-[#ee6018] bg-[#ee6018]/10 px-3 py-1">
              {tag}
            </span>
          {/each}
        </div>
      {/if}

      <!-- Title -->
      <h1 class="font-display text-[36px] md:text-[48px] lg:text-[58px] text-[#020202] tracking-[-0.04em] leading-[1.1] mb-6">
        {title}
      </h1>

      <!-- Meta -->
      <div class="flex flex-wrap items-center gap-4 font-mono text-[14px] text-[#5C5855]">
        <span>{formattedDate}</span>
        <span class="w-1 h-1 bg-[#5C5855] rounded-full"></span>
        <span>{author}</span>
      </div>
    </div>
  </GridContent>
</section>

<!-- Hero Image -->
{#if image}
  <section class="bg-[#0d0d0d] relative">
    <GridLines variant="dark" />
    <GridContent class="py-12">
      <div class="max-w-[800px] mx-auto">
        <img
          src={image}
          alt={title}
          class="w-full h-auto border border-[#333335]"
        />
      </div>
    </GridContent>
  </section>
{/if}

<!-- Article Content -->
<section class="bg-[#0d0d0d] relative">
  <GridLines variant="dark" />

  <!-- Top border with stars -->
  <div class="relative">
    <div class="hidden lg:block absolute top-0 left-0 right-0 h-px bg-[#333335]" aria-hidden="true"></div>
    <Star variant="bottom" class="hidden lg:block absolute left-[calc(9.03%-9px)] -top-px" />
    <Star variant="bottom" class="hidden lg:block absolute right-[calc(9.03%-9px)] -top-px" />
  </div>

  <GridContent class="py-16 lg:py-24">
    <article class="prose-blog max-w-[800px] mx-auto">
      {@render children()}
    </article>
  </GridContent>

  <!-- Bottom border with stars -->
  <div class="relative">
    <div class="hidden lg:block absolute bottom-0 left-0 right-0 h-px bg-[#333335]" aria-hidden="true"></div>
    <Star variant="top" class="hidden lg:block absolute left-[calc(9.03%-9px)] -bottom-px" />
    <Star variant="top" class="hidden lg:block absolute right-[calc(9.03%-9px)] -bottom-px" />
  </div>
</section>

<!-- Footer CTA -->
<section class="bg-[#0d0d0d] py-16 relative">
  <GridLines variant="dark" />
  <GridContent>
    <div class="max-w-[800px] mx-auto text-center">
      <p class="font-mono text-[14px] text-[#878787] mb-4">Want to learn more?</p>
      <a
        href="/news"
        class="inline-flex items-center gap-2 font-mono text-[14px] text-[#ee6018] hover:underline"
      >
        Read more articles
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M6 4L10 8L6 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </a>
    </div>
  </GridContent>
</section>

<style>
  /* Blog prose styling - scoped to this layout */
  :global(.prose-blog) {
    color: #cfcfcf;
    font-family: 'DM Sans', system-ui, sans-serif;
    font-size: 18px;
    line-height: 1.7;
  }

  :global(.prose-blog h2) {
    font-family: 'Space Grotesk', system-ui, sans-serif;
    font-weight: 350;
    font-size: 32px;
    color: white;
    margin-top: 48px;
    margin-bottom: 24px;
    letter-spacing: -0.02em;
  }

  :global(.prose-blog h3) {
    font-family: 'Space Grotesk', system-ui, sans-serif;
    font-weight: 350;
    font-size: 24px;
    color: white;
    margin-top: 36px;
    margin-bottom: 16px;
  }

  :global(.prose-blog p) {
    margin-bottom: 24px;
  }

  :global(.prose-blog a) {
    color: #ee6018;
    text-decoration: none;
    transition: text-decoration 0.2s;
  }

  :global(.prose-blog a:hover) {
    text-decoration: underline;
  }

  :global(.prose-blog strong) {
    color: white;
    font-weight: 500;
  }

  :global(.prose-blog ul),
  :global(.prose-blog ol) {
    margin-bottom: 24px;
    padding-left: 24px;
  }

  :global(.prose-blog li) {
    margin-bottom: 8px;
  }

  :global(.prose-blog ul li::marker) {
    color: #ee6018;
  }

  :global(.prose-blog ol li::marker) {
    color: #ee6018;
    font-family: 'Geist Mono', ui-monospace, monospace;
  }

  :global(.prose-blog blockquote) {
    border-left: 3px solid #ee6018;
    padding-left: 24px;
    margin: 32px 0;
    font-style: italic;
    color: #878787;
  }

  :global(.prose-blog code) {
    font-family: 'Geist Mono', ui-monospace, monospace;
    font-size: 0.9em;
    background: #171719;
    padding: 2px 6px;
    border: 1px solid #333335;
  }

  :global(.prose-blog pre) {
    background: #171719;
    border: 1px solid #333335;
    padding: 24px;
    margin: 32px 0;
    overflow-x: auto;
  }

  :global(.prose-blog pre code) {
    background: none;
    padding: 0;
    border: none;
    font-size: 14px;
    line-height: 1.6;
  }

  :global(.prose-blog img) {
    width: 100%;
    height: auto;
    margin: 32px 0;
    border: 1px solid #333335;
  }

  :global(.prose-blog hr) {
    border: none;
    height: 1px;
    background: #333335;
    margin: 48px 0;
  }
</style>
